import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { RoundPrize } from '@/api/lobby.types';
import i18n from '@/i18n';

const CYCLE_MS = 7500;
const IMAGE_SIZE = 40;

const ordinal = (n: number) => i18n.t('prize.ordinal', { count: n, ordinal: true });

/** "1st" for a single rank, "1st to 3rd" for a band. */
const rankLabel = (prize: RoundPrize) =>
  prize.rankFrom === prize.rankTo
    ? ordinal(prize.rankFrom)
    : i18n.t('prize.range', { from: ordinal(prize.rankFrom), to: ordinal(prize.rankTo) });

/**
 * Cycles a round's prize tiers through the single line the design allows.
 * Self-clocking, so the lobby does not re-render on every tick.
 */
export function CyclingPrize({ prizes }: { prizes: RoundPrize[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
    // Nothing to cycle through with one tier, so no timer is started.
    if (prizes.length < 2) return;
    const id = setInterval(() => setIndex(i => (i + 1) % prizes.length), CYCLE_MS);
    return () => clearInterval(id);
  }, [prizes.length]);

  const prize = prizes[index];
  if (!prize) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={prize.tier}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="flex min-w-0 items-center gap-[4px] font-poppins font-semibold text-[11px] leading-[15px] text-white"
        // One height across the whole cycle: the image size if any tier has one, else text height.
        style={{ height: prizes.some(p => p.imageUrl) ? IMAGE_SIZE : undefined }}
      >
        {prize.imageUrl && (
          <img
            src={prize.imageUrl}
            alt=""
            decoding="sync"
            // Hidden rather than left as a broken-image glyph: the tier still
            // reads correctly from its label without the picture.
            onError={e => {
              e.currentTarget.style.display = 'none';
            }}
            style={{ width: IMAGE_SIZE, height: IMAGE_SIZE }}
            className="shrink-0 rounded-[2px] object-contain"
          />
        )}
        <span className="min-w-0 truncate">
          {rankLabel(prize)} · {prize.name}
        </span>
      </motion.span>
    </AnimatePresence>
  );
}
